import React, { useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';

import { looksLikeDate } from './cardView';

export type CrossFilterSource = {
  title: string;
  rows: Array<Record<string, unknown>>;
  columns: Array<string>;
};

type CrossFilterColumn = {
  name: string;
  cards: Array<string>;
  isDate: boolean;
};

/**
 * Configures filters evaluated against the result rows in the browser.  These
 * are deliberately distinct from dashboard filters, which change the query
 * sent to BigQuery and must be bound to individual cards.
 */
export default function CrossFiltersDialog({
  open,
  sources,
  filterColumns,
  isSaving,
  onClose,
  onSave,
}: {
  open: boolean;
  sources: Array<CrossFilterSource>;
  filterColumns?: Array<string>;
  isSaving: boolean;
  onClose: () => void;
  onSave: (columns: Array<string>) => Promise<void>;
}) {
  const columns = useMemo<Array<CrossFilterColumn>>(() => {
    const byName = new Map<string, CrossFilterColumn>();
    for (const source of sources) {
      for (const name of source.columns) {
        const current = byName.get(name) ?? { name, cards: [], isDate: false };
        current.cards = [...new Set([...current.cards, source.title])];
        current.isDate = current.isDate || looksLikeDate(source.rows, name);
        byName.set(name, current);
      }
    }
    return [...byName.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [sources]);
  const [selected, setSelected] = useState<Array<string>>(filterColumns ?? []);

  useEffect(() => {
    if (!open) {
      return;
    }
    setSelected(filterColumns ?? []);
    // Seed only on the open transition. Keying this on filterColumns would
    // re-seed on every render that hands in a referentially-new-but-equal
    // array, such as a background lens refetch, discarding a checkbox toggle
    // in progress. A lens switch replaces this whole tree with LensLoading
    // while it loads, so reopening always reads the lens actually on screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = (column: string) =>
    setSelected((current) =>
      current.includes(column) ? current.filter((name) => name !== column) : [...current, column],
    );

  return (
    <Dialog fullWidth maxWidth="sm" onClose={isSaving ? undefined : onClose} open={open}>
      <DialogTitle>Cross-filters</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Cross-filters narrow rows already displayed in the Lens. They are instant and do not re-run BigQuery. Use Lens
          filters for values that need to change a card’s query.
        </Alert>
        {!columns.length ? (
          <Typography color="text.secondary">
            There are no loaded result columns yet. Add a query-backed card or wait for the Lens data to load, then try
            again.
          </Typography>
        ) : (
          <List disablePadding>
            {columns.map((column) => (
              <ListItem divider key={column.name} sx={{ px: 0 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      inputProps={{ 'aria-label': column.name.replace(/_/g, ' ') }}
                      checked={selected.includes(column.name)}
                      disabled={column.isDate || isSaving}
                      onChange={() => toggle(column.name)}
                    />
                  }
                  label={
                    <ListItemText
                      primary={column.name.replace(/_/g, ' ')}
                      secondary={
                        column.isDate
                          ? 'Date — use the Lens Period control'
                          : `${column.cards.length} card${column.cards.length === 1 ? '' : 's'}: ${column.cards.join(', ')}`
                      }
                    />
                  }
                  sx={{ m: 0, width: '100%' }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button disabled={isSaving} onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={isSaving || !columns.length} onClick={() => onSave(selected)} variant="contained">
          {isSaving ? 'Saving…' : 'Save cross-filters'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
